import { NetworkStatus } from "@apollo/client";
import { useQuery } from "@apollo/client/react";
import { useMemo } from "react";

import useTypedContext from "hooks/useTypedContext";
import { CloudIntegrationAttachment } from "shared/CloudIntegration/types";

import { StackFormContext } from "../../context";
import { GET_ATTACHED_STACK_INTEGRATIONS } from "../gql";
import { GetAttachedCloudIntegrationsGql } from "../types";

type UseAttachedIntegration = {
  manuallyAttachedIntegrations: CloudIntegrationAttachment[];
  autoAttachedIntegrations: CloudIntegrationAttachment[];
  loading: boolean;
  hasData: boolean;
  refetch: () => void;
};

const useAttachedCloudIntegration = (): UseAttachedIntegration => {
  const { createdStackId } = useTypedContext(StackFormContext);

  const { loading, data, networkStatus, refetch } = useQuery<GetAttachedCloudIntegrationsGql>(
    GET_ATTACHED_STACK_INTEGRATIONS,
    {
      variables: {
        stackId: createdStackId,
      },
      skip: !createdStackId,
      fetchPolicy: "no-cache",
    }
  );

  const integrations = useMemo(() => {
    const awsIntegrations = data?.stack.integrations?.awsV2 || [];
    const azureIntegrations = data?.stack.integrations?.azure || [];

    return ([] as Array<CloudIntegrationAttachment>)
      .concat(awsIntegrations)
      .concat(azureIntegrations);
  }, [data?.stack.integrations?.awsV2, data?.stack.integrations?.azure]);

  const manuallyAttachedIntegrations = useMemo(() => {
    const manualIntegrations = integrations.filter((integration) => {
      if ("isAutoattached" in integration) {
        return !integration.isAutoattached;
      }
      return true; // if not autoattached, it's a manually attached integration
    });
    return manualIntegrations;
  }, [integrations]);

  const autoAttachedIntegrations = useMemo(() => {
    return integrations.filter((integration) => {
      if ("isAutoattached" in integration) {
        return integration.isAutoattached;
      }
      return false;
    });
  }, [integrations]);

  return {
    manuallyAttachedIntegrations,
    autoAttachedIntegrations,
    loading: loading && networkStatus !== NetworkStatus.refetch,
    refetch,
    hasData: !loading && !!data?.stack,
  };
};

export default useAttachedCloudIntegration;
